import { Currency } from '@uniswap/sdk-core'
//import blankTokenUrl from 'assets/svg/blank_token.svg'
import { ReactComponent as UnknownStatus } from 'assets/svg/contract-interaction.svg'
import { LogoImage, MissingImageLogo } from 'components/Logo/AssetLogo'
import { Unicon } from 'components/Unicon'
import { getChainInfo } from 'constants/chainInfo'
import { SupportedChainId } from 'constants/chains'
import useTokenLogoSource from 'hooks/useAssetLogoSource'
import useENSAvatar from 'hooks/useENSAvatar'
import React from 'react'
import { Loader } from 'react-feather'
import styled, { useTheme } from 'styled-components/macro'
const UnknownContract = styled(UnknownStatus)`
  color: ${({ theme }) => theme.textSecondary};
`

const DoubleLogoContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 2px;
  position: relative;
  top: 0;
  left: 0;

  ${LogoImage}, ${MissingImageLogo} {
    width: 19px;
    height: 40px;
    display: block;
    overflow: hidden;
  }

  ${LogoImage}:nth-child(1),
  ${MissingImageLogo}:nth-child(1) {
    border-radius: 20px 0 0 20px;
  }

  ${LogoImage}:nth-child(2),
  ${MissingImageLogo}:nth-child(2) {
    border-radius: 0 20px 20px 0;
  }

  ${LogoImage} {
    object-fit: cover;
  }
  ${LogoImage}:nth-child(1) {
    object-position: 0 0;
  }
  ${LogoImage}:nth-child(2) {
    object-position: 100% 0;
  }
`

type MultiLogoProps = {
  chainId: SupportedChainId
  accountAddress?: string
  currencies?: Array<Currency | undefined>
  images?: (string | undefined)[]
  size?: string
  style?: React.CSSProperties
}

const StyledLogoParentContainer = styled.div`
  position: relative;
  top: 0;
  left: 0;
`

const ENSAvatarImg = styled.img`
  border-radius: 8px;
  height: 40px;
  width: 40px;
`

const StyledChainLogo = styled.img`
  height: 14px;
  width: 14px;
`

const SquareChainLogo = styled.img`
  height: 100%;
  width: 100%;
`

const L2LogoContainer = styled.div<{ $backgroundColor?: string }>`
  background-color: ${({ $backgroundColor }) => $backgroundColor};
  border-radius: 2px;
  height: 16px;
  left: 60%;
  position: absolute;
  top: 60%;
  outline: 2px solid ${({ theme }) => theme.backgroundSurface};
  width: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
`

/**
 * Renders an image by prioritizing a list of sources, and then eventually a fallback triangle alert
 */
export function PortfolioLogo({
  chainId = SupportedChainId.MAINNET,
  accountAddress,
  currencies,
  images,
  size = '40px',
  style,
}: MultiLogoProps) {
  const { squareLogoUrl, logoUrl } = getChainInfo(chainId)
  const chainLogo = squareLogoUrl ?? logoUrl
  const { avatar, loading } = useENSAvatar(accountAddress, false)
  const theme = useTheme()

  const addr0 = (currencies?.[0] as any)?.address as string | undefined
  const addr1 = (currencies?.[1] as any)?.address as string | undefined
  const isNative0 = (currencies?.[0] as any)?.isNative
  const isNative1 = (currencies?.[1] as any)?.isNative

  const [src, nextSrc] = useTokenLogoSource(addr0, chainId, isNative0)
  const [src2, nextSrc2] = useTokenLogoSource(addr1, chainId, isNative1)

  let component
  if (accountAddress) {
    component = loading ? (
      <Loader size={size} />
    ) : avatar ? (
      <ENSAvatarImg src={avatar} alt="avatar" />
    ) : (
      <Unicon size={40} address={accountAddress} />
    )
  } else if (currencies && currencies.length) {
    const safeSymbol = (sym?: string) => sym?.toUpperCase().replace('$', '').replace(/\s+/g, '').slice(0, 3) || ''

    const logo1 = src ? (
      <LogoImage size={size} src={src} onError={nextSrc} />
    ) : currencies?.[0] ? (
      <MissingImageLogo size={size}>{safeSymbol(currencies[0]?.symbol)}</MissingImageLogo>
    ) : images?.[0] ? (
      <LogoImage size={size} src={images[0]} />
    ) : (
      <MissingImageLogo size={size} />
    )

    const logo2 = src2 ? (
      <LogoImage size={size} src={src2} onError={nextSrc2} />
    ) : currencies?.[1] ? (
      <MissingImageLogo size={size}>{safeSymbol(currencies[1]?.symbol)}</MissingImageLogo>
    ) : images && images.length > 1 ? (
      <LogoImage size={size} src={images[images.length - 1]} />
    ) : (
      <MissingImageLogo size={size} />
    )

    component =
      currencies.length > 1 ? (
        <DoubleLogoContainer style={style}>
          {logo1}
          {logo2}
        </DoubleLogoContainer>
      ) : src ? (
        logo1
      ) : (
        <MissingImageLogo size={size}>
          {currencies[0]?.symbol?.toUpperCase().replace('$', '').replace(/\s+/g, '').slice(0, 3)}
        </MissingImageLogo>
      )
  } else if (images && images.length) {
    component =
      images.length > 1 ? (
        <DoubleLogoContainer style={style}>
          <LogoImage size={size} src={images[0]} />
          <LogoImage size={size} src={images[images.length - 1]} />
        </DoubleLogoContainer>
      ) : (
        <LogoImage size={size} src={images[0]} />
      )
  } else {
    return <UnknownContract width={size} height={size} />
  }

  const L2Logo =
    chainId !== SupportedChainId.MAINNET && chainLogo ? (
      <L2LogoContainer $backgroundColor={squareLogoUrl ? theme.backgroundSurface : theme.textPrimary}>
        {squareLogoUrl ? (
          <SquareChainLogo src={chainLogo} alt="chainLogo" />
        ) : (
          <StyledChainLogo src={chainLogo} alt="chainLogo" />
        )}
      </L2LogoContainer>
    ) : null

  return (
    <StyledLogoParentContainer>
      {component}
      {L2Logo}
    </StyledLogoParentContainer>
  )
}
